import { useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "@/lib/auth";
import {
  fetchOrders,
  fetchOrderItems,
  fetchOrdersYearCompare,
  fetchOrdersYtd,
  fetchTeamCommissions,
  fetchCommissionLedger,
  sendInvoiceEmail,
  invoicePdfUrl,
  fetchInvoices,
  updateInvoiceStatus,
  deleteInvoices,
  isAdminUser,
} from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  ShoppingBag,
  Truck,
  DollarSign,
  Loader2,
  Search,
  RefreshCcw,
  Calendar,
  ChevronRight,
  ChevronDown,
  Pencil,
  Trash2,
  TrendingUp,
  TrendingDown,
  Users,
  ShieldCheck,
  BadgePercent,
  Phone,
  Mail,
  Copy,
  Check,
  Receipt,
  Send,
  FileDown,
} from "lucide-react";
import { toast } from "sonner";
import OrderDetailSheet from "@/components/OrderDetailSheet";
import DraftOrderSheet from "@/components/DraftOrderSheet";

function fmt(n) {
  const x = Number(n || 0);
  return x.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: x % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "processing", label: "Processing" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

const SOURCE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "web", label: "Web" },
  { value: "chatgpt", label: "ChatGPT" },
  { value: "dealer", label: "Dealer" },
  { value: "event", label: "Event" },
];

const SCOPE_OPTIONS = [
  { value: "mine", label: "Your orders", icon: ShoppingBag },
  { value: "team", label: "Team orders", icon: Users },
  { value: "invoices", label: "Invoices", icon: Receipt },
  { value: "all", label: "All orders", icon: ShieldCheck, adminOnly: true },
];

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-01`;
};
// Default window for the orders page: last 30 days ending today.
const thirtyDaysAgo = () => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 30);
  return d.toISOString().slice(0, 10);
};

export default function Earnings() {
  const { user } = useAuth();
  const recId = user?.user_record_id || user?.record_id;
  const isAdmin = isAdminUser(user);

  // Admins ALWAYS see all orders — no scope tabs, no team/coupon lookups,
  // no year-compare / YTD / commission rollups. Just one date-bounded query
  // to keep Airtable load minimal. Dealers keep the full UI.
  const [scope, setScope] = useState(isAdmin ? "all" : "mine");
  const [status, setStatus] = useState("all");
  const [source, setSource] = useState("all");
  const [fromDate, setFromDate] = useState(thirtyDaysAgo());
  const [toDate, setToDate] = useState(today());
  // These two filters used to be surfaced as text inputs but were a major
  // source of Airtable rate-limit cascades because every keystroke triggered
  // a fresh full-table query. They are gone now — admins use date range only.
  const dealerUserId = "";
  const couponCode = "";

  const [data, setData] = useState({ orders: [], count: 0, total_value: 0, avg_order_value: 0, is_admin: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [years, setYears] = useState(() => {
    const y = new Date().getUTCFullYear();
    return `${y - 1},${y}`;
  });
  const [yearCompare, setYearCompare] = useState([]);
  const [ytd, setYtd] = useState(null);
  const [teamComm, setTeamComm] = useState(null);
  // Year to compare against for the dashboard card. Defaults to (this year - 1).
  const [compareYear, setCompareYear] = useState(
    () => new Date().getUTCFullYear() - 1
  );

  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  // Invoice management (Invoices tab): multi-select, edit, delete.
  const [selectedInvoices, setSelectedInvoices] = useState(() => new Set());
  const [paymentMethod, setPaymentMethod] = useState(""); // invoices: Cash / Card / Check / ACH / Other
  const [editInvoice, setEditInvoice] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!recId) return;
    setLoading(true);
    setError(null);
    try {
      if (scope === "invoices") {
        const res = await fetchInvoices(recId, { status: status === "all" ? "" : status, paymentMethod });
        setData({
          success: true,
          is_admin: false,
          count: res.count,
          total_value: res.total_value,
          avg_order_value: 0,
          orders: res.invoices || [],
        });
      } else {
        const res = await fetchOrders({
          userRecordId: recId,
          scope,
          status,
          source,
          fromDate,
          toDate,
          limit: 200,
        });
        setData(res);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [recId, scope, status, source, fromDate, toDate, paymentMethod]);

  useEffect(() => {
    setSelectedInvoices(new Set());
  }, [scope]);

  const toggleSelectInvoice = (id) => {
    setSelectedInvoices((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDeleteInvoices = async (ids) => {
    if (!ids.length) return;
    if (
      !window.confirm(
        `Cancel ${ids.length} invoice${ids.length > 1 ? "s" : ""}? Cancelled invoices keep their number, show as CANCELLED and count as $0 (never deleted).`
      )
    )
      return;
    setDeleting(true);
    try {
      await deleteInvoices({ userRecordId: recId, invoiceRecordIds: ids });
      toast.success(`Cancelled ${ids.length} invoice${ids.length > 1 ? "s" : ""}`);
      setSelectedInvoices(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    } finally {
      setDeleting(false);
    }
  };

  const loadYearCompare = useCallback(async () => {
    // Year-compare section is hidden — skip the network call.
    setYearCompare([]);
  }, []);

  const loadYtd = useCallback(async () => {
    if (!recId || scope === "invoices") {
      // YTD summary isn't meaningful for the invoices list; skip the call
      // (the endpoint also doesn't accept the "invoices" scope).
      setYtd(null);
      return;
    }
    try {
      const res = await fetchOrdersYtd({
        userRecordId: recId,
        scope,
        fromDate,
        toDate,
        compareYear,
      });
      setYtd(res);
    } catch {
      setYtd(null);
    }
  }, [recId, scope, fromDate, toDate, compareYear]);

  const loadTeamCommissions = useCallback(async () => {
    if (!recId || scope !== "team") {
      setTeamComm(null);
      return;
    }
    try {
      const res = await fetchTeamCommissions(recId);
      setTeamComm(res);
    } catch {
      setTeamComm(null);
    }
  }, [recId, scope]);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!active) return;
      await loadTeamCommissions();
    })();
    return () => {
      active = false;
    };
  }, [loadTeamCommissions]);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!active) return;
      await loadYtd();
    })();
    return () => {
      active = false;
    };
  }, [loadYtd]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        if (!active) return;
        await load();
      } catch {
        /* swallow */
      }
    })();
    return () => {
      active = false;
    };
  }, [load]);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!active) return;
      await loadYearCompare();
    })();
    return () => {
      active = false;
    };
  }, [loadYearCompare]);

  const scopeOptions = useMemo(
    () => SCOPE_OPTIONS.filter((s) => !s.adminOnly || data.is_admin),
    [data.is_admin]
  );

  const [openWithEdit, setOpenWithEdit] = useState(false);

  const handleRowClick = (orderRecId, { edit = false } = {}) => {
    setSelectedOrderId(orderRecId);
    setOpenWithEdit(edit);
    setSheetOpen(true);
  };

  return (
    <div className="px-4 pt-4 pb-32" data-testid="earnings-page">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">
          Orders
        </h1>
        <Button
          variant="ghost"
          size="sm"
          onClick={load}
          disabled={loading}
          className="rounded-full text-xs"
          data-testid="orders-refresh-btn"
        >
          <RefreshCcw size={13} className={loading ? "animate-spin" : ""} />
        </Button>
      </div>
      <p className="text-sm text-zinc-500 mb-4 hidden">
        {isAdmin && "All orders across the brand. Filter by date range."}
        {!isAdmin && scope === "mine" && "Orders you placed personally."}
        {!isAdmin && scope === "team" && "Orders placed using your coupon codes."}
        {!isAdmin && scope === "invoices" && "Invoices you created for your customers. Open one to share it."}
        {!isAdmin && scope === "all" && "All orders across the brand (admin view)."}
      </p>

      {/* Stat cards removed per user request */}

      {/* Filters */}
      <Card className="rounded-2xl border-zinc-200 p-4 mb-4 space-y-3" data-testid="orders-filters">
        {/* Scope tabs — dealers can switch between their own and team orders */}
        {!isAdmin && scopeOptions.length > 1 && (
          <div className="flex flex-wrap gap-1.5" data-testid="orders-scope">
            {scopeOptions.map((opt) => {
              const active = scope === opt.value;
              const Icon = opt.icon;
              return (
                <button
                  key={opt.value}
                  onClick={() => setScope(opt.value)}
                  className={`inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-3 py-1 border transition-colors ${
                    active
                      ? "bg-violet-600 text-white border-violet-600"
                      : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"
                  }`}
                  data-testid={`orders-scope-${opt.value}`}
                >
                  {Icon && <Icon size={12} />}
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Status pills */}
        <div className="flex flex-wrap gap-1.5">
          {STATUS_OPTIONS.map((opt) => {
            const active = status === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => setStatus(opt.value)}
                className={`text-[11px] font-semibold rounded-full px-3 py-1 border transition-colors ${
                  active
                    ? "bg-zinc-900 text-white border-zinc-900"
                    : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"
                }`}
                data-testid={`orders-status-${opt.value}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* Source — where the order came from (Web shop, ChatGPT Instant Checkout, Dealer-entered, Event) */}
        <div className="flex flex-wrap items-center gap-1.5" data-testid="orders-source">
          <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold mr-1">Source</span>
          {SOURCE_OPTIONS.map((opt) => {
            const active = source === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => setSource(opt.value)}
                className={`text-[11px] font-semibold rounded-full px-3 py-1 border transition-colors ${
                  active
                    ? "bg-sky-600 text-white border-sky-600"
                    : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"
                }`}
                data-testid={`orders-source-${opt.value}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* Date range */}
        <div className="grid grid-cols-2 gap-2">
          <Field label="From">
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="h-9 rounded-lg text-sm"
              data-testid="orders-from-date"
            />
          </Field>
          <Field label="To">
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="h-9 rounded-lg text-sm"
              data-testid="orders-to-date"
            />
          </Field>
        </div>

        {scope === "all" && data.is_admin && false && (
          <div className="grid grid-cols-2 gap-2 pt-1 border-t border-zinc-100">
            <Field label="Dealer user_id">
              <Input
                value={dealerUserId}
                readOnly
                className="h-9 rounded-lg text-sm"
                data-testid="orders-dealer-uid"
              />
            </Field>
            <Field label="Coupon code">
              <Input
                value={couponCode}
                readOnly
                className="h-9 rounded-lg text-sm"
                data-testid="orders-coupon-code"
              />
            </Field>
          </div>
        )}
      </Card>

      {/* Team Commissions panel (only on Team scope) */}
      {scope === "team" && teamComm && (
        <Card
          className="rounded-2xl border-zinc-200 p-4 mb-4"
          data-testid="orders-team-commissions"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-widest text-violet-700 font-semibold flex items-center gap-1">
              <BadgePercent size={11} /> Team commissions
            </div>
            <div className="text-[10px] text-zinc-500">
              {teamComm.team_size} downline ambassador{teamComm.team_size === 1 ? "" : "s"}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="rounded-xl bg-gradient-to-br from-violet-50 to-white border border-violet-200 p-3" data-testid="team-comm-revenue">
              <div className="text-[10px] uppercase tracking-wider text-violet-700 font-semibold">
                Team revenue
              </div>
              <div className="font-semibold text-zinc-900 text-lg tracking-tight mt-0.5">
                {fmt(teamComm.total_revenue)}
              </div>
            </div>
            <div className="rounded-xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 p-3" data-testid="team-comm-due">
              <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">
                Commission due
              </div>
              <div className="font-semibold text-zinc-900 text-lg tracking-tight mt-0.5">
                {fmt(teamComm.total_commission_due)}
              </div>
            </div>
          </div>

          {(teamComm.rows || []).length === 0 ? (
            <div className="text-xs text-zinc-500 text-center py-3" data-testid="team-comm-empty">
              No downline activity in this date range.
            </div>
          ) : (
            <ul className="space-y-1.5" data-testid="team-comm-list">
              {teamComm.rows.slice(0, 12).map((r) => {
                const name = `${r.first_name || ""} ${r.last_name || ""}`.trim() || r.email || "—";
                const initials = name
                  .split(" ")
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((w) => w[0]?.toUpperCase())
                  .join("");
                return (
                  <li
                    key={r.user_record_id}
                    className="flex items-center gap-3 rounded-xl bg-zinc-50/60 px-3 py-2"
                    data-testid={`team-comm-row-${r.user_record_id}`}
                  >
                    {r.image_url ? (
                      <img src={r.image_url} alt={name} className="w-8 h-8 rounded-full object-cover shrink-0" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-[11px] font-semibold shrink-0">
                        {initials || "?"}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-zinc-900 truncate">{name}</div>
                      <div className="text-[11px] text-zinc-500">
                        {r.order_count} order{r.order_count === 1 ? "" : "s"} · {fmt(r.revenue)} · {r.commission_perc}%
                      </div>
                    </div>
                    <div className="text-sm font-semibold text-emerald-700 shrink-0">
                      {fmt(r.commission_due)}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      )}

      {/* Selected-period vs same period in a chosen year */}
      {ytd && (
        <Card
          className="rounded-2xl border-zinc-200 p-4 mb-4"
          data-testid="orders-ytd-card"
        >
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold flex items-center gap-1">
              <TrendingUp size={11} /> Selected period · vs same period
            </div>
            <div className="flex items-center gap-2">
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                Compare year
              </label>
              <Input
                type="number"
                value={compareYear}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!Number.isNaN(v) && v >= 1900 && v <= 2100) setCompareYear(v);
                  else if (e.target.value === "") setCompareYear("");
                }}
                className="h-7 w-20 rounded-lg text-xs"
                data-testid="orders-compare-year-input"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div
              className="rounded-xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 p-3"
              data-testid="ytd-this-year"
            >
              <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">
                {ytd.this_year?.year} selected
              </div>
              <div className="font-semibold text-zinc-900 text-lg tracking-tight mt-0.5">
                {fmt(ytd.this_year?.total_value)}
              </div>
              <div className="text-[11px] text-zinc-500">
                {ytd.this_year?.count} order{ytd.this_year?.count === 1 ? "" : "s"}
              </div>
            </div>
            <div
              className="rounded-xl bg-gradient-to-br from-zinc-50 to-white border border-zinc-200 p-3"
              data-testid="ytd-last-year"
            >
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                {ytd.last_year?.year} same period
              </div>
              <div className="font-semibold text-zinc-900 text-lg tracking-tight mt-0.5">
                {fmt(ytd.last_year?.total_value)}
              </div>
              <div className="text-[11px] text-zinc-500">
                {ytd.last_year?.count} order{ytd.last_year?.count === 1 ? "" : "s"}
              </div>
            </div>
          </div>
          {ytd.delta_pct !== null && ytd.delta_pct !== undefined && (
            <div
              className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
                ytd.delta_total >= 0
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-rose-50 text-rose-700"
              }`}
              data-testid="ytd-delta-pill"
            >
              {ytd.delta_total >= 0 ? (
                <TrendingUp size={12} />
              ) : (
                <TrendingDown size={12} />
              )}
              {ytd.delta_total >= 0 ? "+" : ""}
              {fmt(ytd.delta_total)} ({ytd.delta_pct > 0 ? "+" : ""}
              {ytd.delta_pct}% YoY)
            </div>
          )}
        </Card>
      )}

      {/* Year-over-year compare section removed per user request */}
      {false && yearCompare.length > 0 && (
        <Card className="rounded-2xl" data-testid="orders-year-compare"><div /></Card>
      )}

      {error && (
        <Card
          className="p-3 mb-3 border-red-200 bg-red-50 text-sm text-red-700"
          data-testid="orders-error"
        >
          {error}
        </Card>
      )}

      {/* Orders header */}
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="text-sm font-semibold text-zinc-900" data-testid="orders-table-title">
          {scope === "invoices" ? "Invoices" : "Orders"} ({fromDate} to {toDate})
        </div>
        <div className="flex items-center gap-2">
          {scope === "invoices" && (
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="h-7 rounded-md border border-zinc-200 bg-white px-1.5 text-[11px]"
              data-testid="invoices-payment-filter"
              title="Filter by payment method"
            >
              <option value="">All payments</option>
              {["Cash", "Credit/Debit Card", "Check", "ACH/Bank Transfer", "Other"].map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          )}
          <div className="text-xs text-zinc-500">{data.count} results</div>
        </div>
      </div>

      {loading ? (
        <div className="py-12 flex flex-col items-center text-zinc-500">
          <Loader2 className="animate-spin mb-2" />
          <span className="text-sm">Loading orders…</span>
        </div>
      ) : data.orders.length === 0 ? (
        <Card
          className="rounded-2xl border-dashed border-zinc-300 p-8 text-center"
          data-testid="orders-empty"
        >
          <Search className="mx-auto mb-2 text-zinc-400" size={20} />
          <div className="font-semibold text-zinc-900 mb-1">No orders found</div>
          <p className="text-sm text-zinc-500 max-w-xs mx-auto">
            Try widening the date range or clearing the status filter.
          </p>
        </Card>
      ) : (
        <ul className="space-y-2" data-testid="orders-list">
          {scope === "invoices" && (
            <li className="flex items-center justify-between px-1 pb-1">
              <span className="text-xs text-zinc-500 flex items-center gap-2" data-testid="invoices-count">
                {data.orders.length} invoice{data.orders.length === 1 ? "" : "s"}
                {selectedInvoices.size > 0 ? ` · ${selectedInvoices.size} selected` : ""}
              </span>
              {selectedInvoices.size > 0 && (
                <button
                  type="button"
                  disabled={deleting}
                  onClick={() => handleDeleteInvoices([...selectedInvoices])}
                  className="h-8 px-3 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-xs font-semibold flex items-center gap-1.5"
                  data-testid="invoices-bulk-delete"
                >
                  <Trash2 size={13} /> Cancel {selectedInvoices.size}
                </button>
              )}
            </li>
          )}
          {data.orders.map((o) => (
            <OrderRow
              key={o.id}
              order={o}
              isAdmin={data.is_admin}
              userRecordId={recId}
              expanded={expandedOrderId === o.id}
              onToggle={() =>
                setExpandedOrderId((cur) => (cur === o.id ? null : o.id))
              }
              onEdit={() => handleRowClick(o.id, { edit: data.is_admin })}
              canShare={scope === "invoices"}
              selectable={scope === "invoices"}
              selected={selectedInvoices.has(o.id)}
              onSelectToggle={toggleSelectInvoice}
              onEditInvoice={(inv) => setEditInvoice(inv)}
              onDeleteInvoice={(inv) => handleDeleteInvoices([inv.id])}
            />
          ))}
        </ul>
      )}

      {/* Commission ledger — hidden on the Invoices tab (invoices aren't orders) */}
      {recId && scope !== "invoices" && <CommissionLedger recId={recId} />}

      <DraftOrderSheet
        open={!!editInvoice}
        onOpenChange={(o) => !o && setEditInvoice(null)}
        customer={null}
        callerUserRecordId={recId}
        dealerMode
        editInvoice={editInvoice}
        onCreated={() => {
          setEditInvoice(null);
          load();
        }}
      />

      <OrderDetailSheet
        orderRecordId={selectedOrderId}
        userRecordId={recId}
        open={sheetOpen}
        isAdmin={data.is_admin}
        initialEdit={openWithEdit}
        onUpdated={() => load()}
        onOpenChange={(v) => {
          setSheetOpen(v);
          if (!v) {
            setSelectedOrderId(null);
            setOpenWithEdit(false);
          }
        }}
      />
    </div>
  );
}

function CommissionLedger({ recId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!recId) return;
    let active = true;
    fetchCommissionLedger(recId)
      .then((d) => active && setData(d || {}))
      .catch(() => active && setData({}));
    return () => {
      active = false;
    };
  }, [recId]);

  if (!data) return null;
  const orders = data.orders || [];
  const payouts = data.entries || [];
  if (orders.length === 0 && payouts.length === 0) return null;

  const grossEarned = Number(data.gross_earned || 0);
  const shippingTotal = Number(data.shipping_total || 0);
  const csPaidShipping = Number(data.cs_paid_shipping_total || 0);
  const netShippingCost = Number(
    data.net_shipping_cost != null ? data.net_shipping_cost : shippingTotal - csPaidShipping
  );
  const totalUsed = Number(data.total_paid || 0);
  const net = Number(data.net_commission || 0);

  return (
    <Card className="rounded-2xl border-zinc-200 p-4 mt-6" data-testid="commission-ledger">
      <div className="flex items-center gap-2 mb-1">
        <BadgePercent size={16} className="text-violet-600" />
        <span className="text-sm font-bold text-zinc-900">Commission ledger</span>
      </div>
      <p className="text-[11px] text-zinc-500 mb-3">
        Every order earns commission. Shipping &amp; commission you spend are taken off.
      </p>

      {/* Orders — credit (earned) + debit (shipping) */}
      {orders.length > 0 && (
        <div className="rounded-xl border border-zinc-100 overflow-hidden mb-3">
          <div className="grid grid-cols-[1fr_auto_auto_auto] gap-2 px-3 py-2 bg-zinc-50 text-[10px] uppercase tracking-wider font-semibold text-zinc-500">
            <span>Order</span>
            <span className="text-right w-16">CS paid ship</span>
            <span className="text-right w-20">Earned</span>
            <span className="text-right w-20">Shipping</span>
          </div>
          <ul className="divide-y divide-zinc-100">
            {orders.map((o, i) => (
              <li
                key={o.order || `order-${i}`}
                className="grid grid-cols-[1fr_auto_auto_auto] gap-2 px-3 py-2.5 items-center"
                data-testid={`ledger-order-${i}`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-zinc-900 truncate">
                    {o.customer || o.order}
                  </div>
                  <div className="text-[11px] text-zinc-500 truncate">
                    {o.order} · {o.date} · {o.items} item{o.items === 1 ? "" : "s"}
                  </div>
                </div>
                <span
                  className="text-right w-16 text-sm font-semibold text-blue-700"
                  data-testid={`ledger-order-cspaid-${i}`}
                >
                  {Number(o.cs_paid_shipping) > 0 ? fmt(o.cs_paid_shipping) : "—"}
                </span>
                <span className="text-right w-20 text-sm font-semibold text-emerald-700">
                  +{fmt(o.earned)}
                </span>
                <span className="text-right w-20 text-sm font-semibold text-rose-600">
                  −{fmt(o.shipping)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Payouts — debit (commission spent at checkout) */}
      {payouts.length > 0 && (
        <div className="rounded-xl border border-zinc-100 overflow-hidden mb-3">
          <div className="px-3 py-2 bg-zinc-50 text-[10px] uppercase tracking-wider font-semibold text-zinc-500">
            Commission you used
          </div>
          <ul className="divide-y divide-zinc-100">
            {payouts.map((e, i) => (
              <li
                key={`${e.order || "payout"}-${e.date || i}`}
                className="flex items-start justify-between px-3 py-2.5 gap-2"
                data-testid={`ledger-payout-${i}`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-zinc-900 truncate">
                    {e.customer || e.order || "Commission redeemed"}
                  </div>
                  <div className="text-[11px] text-zinc-500 truncate">
                    {[e.order, e.date].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-semibold text-rose-600">
                    −{fmt(e.amount)} <span className="text-[10px] font-normal text-zinc-400">commission</span>
                  </div>
                  {Number(e.shipping) > 0 && (
                    <div className="text-[11px] font-semibold text-rose-500">
                      −{fmt(e.shipping)} <span className="text-[10px] font-normal text-zinc-400">shipping</span>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Totals */}
      <div className="space-y-1.5 text-sm px-1">
        <Row label="Total earned" value={`+${fmt(grossEarned)}`} tone="emerald" testId="ledger-total-earned" />
        <Row label="Shipping paid by customers" value={`+${fmt(csPaidShipping)}`} tone="blue" testId="ledger-cs-paid-shipping" />
        <Row label="Shipping costs" value={`−${fmt(shippingTotal)}`} tone="rose" testId="ledger-total-shipping" />
        <Row label="Net shipping cost" value={`−${fmt(netShippingCost)}`} tone="rose" testId="ledger-net-shipping" />
        <Row label="Commission used" value={`−${fmt(totalUsed)}`} tone="rose" testId="ledger-total-used" />
        <div className="flex justify-between pt-2 mt-1 border-t border-zinc-200 text-base font-bold text-zinc-900">
          <span>Net available</span>
          <span className="text-emerald-700" data-testid="ledger-net">{fmt(net)}</span>
        </div>
      </div>
    </Card>
  );
}

function Row({ label, value, tone, testId }) {
  const c =
    tone === "emerald"
      ? "text-emerald-700"
      : tone === "blue"
        ? "text-blue-700"
        : "text-rose-600";
  return (
    <div className="flex justify-between text-zinc-500">
      <span>{label}</span>
      <span className={`font-semibold ${c}`} data-testid={testId}>{value}</span>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] font-medium text-zinc-600">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function StatCard({ label, value, tone, icon: Icon, testId }) {
  const tones = {
    emerald: "from-emerald-500 to-emerald-700",
    violet: "from-violet-500 to-violet-700",
    indigo: "from-indigo-500 to-indigo-700",
  };
  return (
    <Card
      className={`rounded-2xl border-0 p-3 bg-gradient-to-br ${tones[tone]} text-white`}
      data-testid={testId}
    >
      <Icon size={14} className="opacity-80" />
      <div className="mt-2 text-[10px] uppercase tracking-wider opacity-80">
        {label}
      </div>
      <div className="font-semibold tracking-tight text-base sm:text-lg truncate">
        {value}
      </div>
    </Card>
  );
}

function OrderRow({ order, isAdmin, userRecordId, expanded, onToggle, onEdit, canShare, selectable, selected, onSelectToggle, onEditInvoice, onDeleteInvoice }) {
  const buyer =
    order.name ||
    [order.customer_first_name, order.customer_last_name]
      .filter(Boolean)
      .join(" ") ||
    "Customer";

  const s = (order.status || "").toString().toLowerCase();
  const tone = {
    completed: "bg-emerald-50 text-emerald-700",
    processing: "bg-blue-50 text-blue-700",
    pending: "bg-amber-50 text-amber-700",
    cancelled: "bg-rose-50 text-rose-700",
    refunded: "bg-zinc-100 text-zinc-700",
  }[s] || "bg-zinc-100 text-zinc-700";

  return (
    <li
      className={`rounded-xl bg-white border transition-colors ${
        expanded
          ? "border-violet-300 ring-1 ring-violet-200/60"
          : "border-zinc-200 hover:border-violet-300 hover:bg-violet-50/30"
      }`}
      data-testid={`order-row-${order.id}`}
    >
      <div className="flex items-center">
        {selectable && (
          <label
            className="pl-3 pr-1 py-3 flex items-center cursor-pointer shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={!!selected}
              onChange={() => onSelectToggle?.(order.id)}
              className="w-4 h-4 accent-violet-600"
              data-testid={`invoice-select-${order.id}`}
            />
          </label>
        )}
        <button
          type="button"
          onClick={onToggle}
          className="flex-1 min-w-0 text-left p-3 flex items-center gap-2 sm:gap-3"
          data-testid={`order-row-toggle-${order.id}`}
        >
        <div className="hidden sm:flex w-10 h-10 rounded-lg bg-zinc-100 items-center justify-center shrink-0">
          <ShoppingBag className="text-zinc-700" size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-medium text-zinc-900 truncate">{buyer}</div>
            {order.source_label && (
              <span
                className="text-[10px] uppercase tracking-wider font-semibold bg-sky-50 text-sky-700 px-1.5 py-0.5 rounded"
                data-testid={`order-source-${order.id}`}
              >
                {order.source_label}
              </span>
            )}
            {order.payment_label && (
              <span
                className="text-[10px] uppercase tracking-wider font-semibold bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded"
                data-testid={`order-payment-${order.id}`}
              >
                {order.payment_label}
              </span>
            )}
            {order.coupon_code && (
              <span className="text-[10px] uppercase tracking-wider font-semibold bg-violet-50 text-violet-700 px-1.5 py-0.5 rounded font-mono">
                {order.coupon_code}
              </span>
            )}
            {order.tracking_number && (
              <a
                href={order.tracking_url || "#"}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-[10px] font-mono font-semibold bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded hover:bg-amber-100"
                title="Track with UPS"
                data-testid={`order-tracking-${order.id}`}
              >
                <Truck size={10} /> UPS {order.tracking_number}
              </a>
            )}
          </div>
          <div className="text-xs text-zinc-500 flex items-center gap-1.5 min-w-0 flex-wrap">
            <span className="shrink-0">{order.order_ref ? <><span className="font-semibold text-amber-700" data-testid={`order-ref-${order.id}`}>{order.order_ref}</span> <span className="text-zinc-400">(#{order.order_id}, from #{order.parent_post_id})</span></> : `#${order.order_id}`}</span>
            <span className="shrink-0">·</span>
            <span className="shrink-0">{order.date}</span>
            {order.status && (
              <>
                <span className="shrink-0">·</span>
                <span
                  className={`shrink-0 whitespace-nowrap text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${tone}`}
                >
                  {order.status}
                </span>
              </>
            )}
            {order.paid_at && (
              <span className="shrink-0 whitespace-nowrap text-[10px] font-semibold text-emerald-700" data-testid={`invoice-paid-at-${order.id}`}>
                paid {order.paid_at}
              </span>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className={`font-semibold text-sm sm:text-base ${order.cancelled ? "text-rose-600" : "text-zinc-900"}`}>
            {order.cancelled ? "CANCELLED" : fmt(order.total)}
          </div>
          {order.cancelled && order.original_total ? (
            <div className="text-[10px] text-zinc-400 line-through">{fmt(order.original_total)}</div>
          ) : null}
          {order.payment_method ? (
            <div className="text-[10px] text-emerald-700" data-testid={`invoice-payment-${order.id}`}>{order.payment_method}</div>
          ) : null}
          <div className="text-[10px] text-zinc-400">
            {order.item_count
              ? `${order.item_count} item${order.item_count > 1 ? "s" : ""}`
              : ""}
          </div>
        </div>
        {expanded ? (
          <ChevronDown className="text-violet-500 shrink-0" size={16} />
        ) : (
          <ChevronRight className="text-zinc-300 shrink-0" size={16} />
        )}
        </button>
      </div>

      {expanded && (
        <OrderInlineItems
          orderRecordId={order.id}
          userRecordId={userRecordId}
          order={order}
          isAdmin={isAdmin}
          onEdit={onEdit}
          canShare={canShare}
          onEditInvoice={onEditInvoice}
          onDeleteInvoice={onDeleteInvoice}
        />
      )}
    </li>
  );
}

function CopyContact({ icon: Icon, value, href, label, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy");
    }
  };
  return (
    <div
      className="flex items-center gap-1.5 rounded-lg bg-white border border-zinc-200 pl-2 pr-1 py-1 text-xs"
      data-testid={testId}
    >
      <Icon size={13} className="text-zinc-400 shrink-0" />
      <a
        href={href}
        onClick={(e) => e.stopPropagation()}
        className="text-zinc-700 hover:text-violet-700 truncate max-w-[180px]"
      >
        {value}
      </a>
      <button
        type="button"
        onClick={copy}
        className="w-6 h-6 rounded-md hover:bg-zinc-100 flex items-center justify-center text-zinc-400 hover:text-violet-700 shrink-0"
        aria-label={`Copy ${label}`}
        data-testid={`${testId}-copy`}
      >
        {copied ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
      </button>
    </div>
  );
}

function OrderInlineItems({ orderRecordId, userRecordId, order, isAdmin, onEdit, canShare, onEditInvoice, onDeleteInvoice }) {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [invStatus, setInvStatus] = useState(order.status || "pending");
  const [savingStatus, setSavingStatus] = useState(false);
  const [paidPick, setPaidPick] = useState("");

  const changeStatus = async (next, paidAt) => {
    if (next.toLowerCase() === "paid" && !paidAt) {
      setPaidPick(new Date().toISOString().slice(0, 10));
      return;
    }
    if (savingStatus) return;
    setSavingStatus(true);
    const prev = invStatus;
    setInvStatus(next);
    setPaidPick("");
    try {
      const res = await updateInvoiceStatus({
        userRecordId,
        invoiceRecordId: orderRecordId,
        status: next,
        paidAt,
      });
      setInvStatus(res.status || next);
      toast.success(paidAt ? `Invoice marked paid on ${paidAt}` : `Invoice marked ${res.status || next}`);
    } catch (e2) {
      setInvStatus(prev);
      toast.error(e2?.response?.data?.detail || "Could not update status");
    } finally {
      setSavingStatus(false);
    }
  };

  const emailInvoice = async (e) => {
    e.stopPropagation();
    let to = (order.customer_email || "").trim();
    if (!to) {
      to = (window.prompt("Send this invoice to which email?") || "").trim();
      if (!to) return;
    }
    setSending(true);
    try {
      const res = await sendInvoiceEmail({
        userRecordId,
        invoiceRecordId: orderRecordId,
        toEmail: to,
      });
      setSent(true);
      setTimeout(() => setSent(false), 2500);
      toast.success(`Invoice emailed to ${res.to_email || to}`);
    } catch (e2) {
      toast.error(
        e2?.response?.data?.detail || e2.message || "Could not send invoice"
      );
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    // Invoices ship their line items inline (no separate items endpoint).
    if (Array.isArray(order.items)) {
      setItems(order.items);
      setErr(null);
      return;
    }
    let active = true;
    (async () => {
      try {
        const res = await fetchOrderItems(orderRecordId, userRecordId);
        if (!active) return;
        setItems(res?.items || []);
        setErr(null);
      } catch (e) {
        if (!active) return;
        setErr(e?.response?.data?.detail || e.message || "Could not load items");
      }
    })();
    return () => {
      active = false;
    };
  }, [orderRecordId, userRecordId, order.items]);

  return (
    <div
      className="border-t border-zinc-200 px-3 py-3 bg-zinc-50/40 space-y-2"
      data-testid={`order-row-items-${orderRecordId}`}
    >
      {(order.phone || order.customer_email) && (
        <div className="flex flex-wrap gap-2 pb-1" data-testid={`order-contact-${orderRecordId}`}>
          {order.phone && (
            <CopyContact
              icon={Phone}
              value={order.phone}
              href={`tel:${order.phone}`}
              label="Phone"
              testId={`order-phone-${orderRecordId}`}
            />
          )}
          {order.customer_email && (
            <CopyContact
              icon={Mail}
              value={order.customer_email}
              href={`mailto:${order.customer_email}`}
              label="Email"
              testId={`order-email-${orderRecordId}`}
            />
          )}
        </div>
      )}
      {items === null && !err && (
        <div className="text-xs text-zinc-500 flex items-center gap-1.5">
          <Loader2 className="animate-spin" size={11} /> Loading items…
        </div>
      )}
      {err && (
        <div className="text-xs text-rose-600">{err}</div>
      )}
      {items && items.length === 0 && (
        <div className="text-xs text-zinc-500 italic">No line items recorded.</div>
      )}
      {items && items.length > 0 && (
        <ul className="space-y-1.5">
          {items.map((it) => (
            <li
              key={it.id}
              className="flex items-center gap-2 text-xs text-zinc-700"
              data-testid={`order-inline-item-${it.id}`}
            >
              {it.image_url ? (
                <img
                  src={it.image_url}
                  alt={it.name}
                  className="w-7 h-7 rounded object-cover bg-zinc-100 shrink-0"
                  onError={(e) => (e.currentTarget.style.display = "none")}
                />
              ) : (
                <div className="w-7 h-7 rounded bg-zinc-100 flex items-center justify-center text-zinc-400 shrink-0">
                  <ShoppingBag size={12} />
                </div>
              )}
              <div className="flex-1 min-w-0 truncate">{it.name}</div>
              <div className="text-zinc-500 shrink-0">×{it.qty}</div>
              <div className="font-semibold text-zinc-900 shrink-0 w-16 text-right">
                {fmt(it.line_total)}
              </div>
            </li>
          ))}
        </ul>
      )}
      {order.tracking_number ? (
        <a
          href={order.tracking_url || "#"}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-2 text-xs rounded-lg bg-blue-50 text-blue-700 px-2.5 py-1.5 hover:bg-blue-100 transition-colors"
          data-testid={`order-tracking-${orderRecordId}`}
        >
          <Truck size={13} className="shrink-0" />
          <span className="font-semibold">Track:</span>
          <span className="font-mono truncate">{order.tracking_number}</span>
        </a>
      ) : null}
      <div className="pt-2 border-t border-zinc-200/60 flex items-center justify-between text-xs">
        <div className="space-x-3 text-zinc-500">
          {order.shipping_charge ? <span>ship {fmt(order.shipping_charge)}</span> : null}
          {Number(order.tax || 0) > 0 ? <span>tax {fmt(order.tax)}</span> : null}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-zinc-500">Total</span>
          <span className={`font-semibold ${order.cancelled ? "text-rose-600 uppercase" : "text-zinc-900"}`}>{order.cancelled ? "Cancelled · $0.00" : fmt(order.total)}</span>
          {canShare ? null : isAdmin ? (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className="h-7 rounded-lg text-xs border-violet-200 text-violet-700 hover:bg-violet-50 ml-2"
              data-testid={`order-row-edit-${orderRecordId}`}
            >
              <Pencil size={11} className="mr-1" />
              Edit
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className="h-7 rounded-lg text-xs border-violet-200 text-violet-700 hover:bg-violet-50 ml-2"
              data-testid={`order-row-view-${orderRecordId}`}
            >
              View order
            </Button>
          )}
        </div>
      </div>
      {canShare && (
        <div
          className="mt-2 flex items-center gap-1 flex-wrap"
          onClick={(e) => e.stopPropagation()}
          data-testid={`invoice-status-${orderRecordId}`}
        >
          <span className="text-[11px] text-zinc-500 mr-1">Status</span>
          {["pending", "processing", "Paid", "cancelled"].map((s) => {
            const active = invStatus.toLowerCase() === s.toLowerCase();
            return (
              <button
                key={s}
                type="button"
                disabled={savingStatus}
                onClick={() => changeStatus(s)}
                className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide transition-colors disabled:opacity-50 ${
                  active
                    ? "bg-violet-600 text-white"
                    : "bg-zinc-100 text-zinc-500 hover:bg-zinc-200"
                }`}
                data-testid={`invoice-status-${s.toLowerCase()}-${orderRecordId}`}
              >
                {s === "Paid" ? "Paid" : s}
              </button>
            );
          })}
          <div className="flex-1" />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEditInvoice?.(order);
            }}
            className="px-2 py-1 rounded-lg text-[11px] font-semibold text-violet-700 hover:bg-violet-50 flex items-center gap-1"
            data-testid={`invoice-edit-${orderRecordId}`}
          >
            <Pencil size={12} /> Edit
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteInvoice?.(order);
            }}
            className="px-2 py-1 rounded-lg text-[11px] font-semibold text-rose-600 hover:bg-rose-50 flex items-center gap-1"
            data-testid={`invoice-delete-${orderRecordId}`}
          >
            <Trash2 size={12} /> Cancel
          </button>
        </div>
      )}
      {canShare && paidPick && (
        <div className="mt-2 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-2.5 py-2" onClick={(e) => e.stopPropagation()} data-testid={`invoice-paid-date-${orderRecordId}`}>
          <span className="text-[11px] font-semibold text-emerald-800 whitespace-nowrap">Paid on</span>
          <input type="date" value={paidPick} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setPaidPick(e.target.value)} className="h-8 flex-1 min-w-0 rounded-lg border border-emerald-200 bg-white px-2 text-xs" data-testid={`invoice-paid-date-input-${orderRecordId}`} />
          <button type="button" disabled={savingStatus || !paidPick} onClick={() => changeStatus("Paid", paidPick)} className="h-8 px-3 rounded-lg bg-emerald-600 text-white text-[11px] font-semibold disabled:opacity-50" data-testid={`invoice-paid-date-save-${orderRecordId}`}>Save</button>
          <button type="button" onClick={() => setPaidPick("")} className="h-8 px-2 rounded-lg text-[11px] font-semibold text-zinc-500" data-testid={`invoice-paid-date-cancel-${orderRecordId}`}>Cancel</button>
        </div>
      )}
      {canShare && (
        <div className="mt-1 flex items-center gap-2">
          <button
            type="button"
            onClick={emailInvoice}
            disabled={items === null || sending}
            className="flex-1 h-9 rounded-lg bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors"
            data-testid={`invoice-email-${orderRecordId}`}
          >
            {sending ? (
              <Loader2 size={13} className="animate-spin" />
            ) : sent ? (
              <Check size={13} />
            ) : (
              <Send size={13} />
            )}
            {sending ? "Sending…" : sent ? "Sent" : "Email invoice"}
          </button>
          <a
            href={invoicePdfUrl(userRecordId, orderRecordId)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="h-9 px-3 rounded-lg border border-violet-200 text-violet-700 hover:bg-violet-50 text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors shrink-0"
            data-testid={`invoice-pdf-${orderRecordId}`}
          >
            <FileDown size={13} /> PDF
          </a>
        </div>
      )}
    </div>
  );
}
